import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { flagLookupsApi, siteUsersApi } from '../../api/flagSystemApi';
import {
  FLAG_ROLE_LABELS, SITE_ROLES, flagError, flagFieldErrors, siteUserRequest, validateSiteUser,
} from '../../utils/flagSystem';
import { Feedback, Field, FlagPage, ResourceState, useRemoteResource } from './FlagSystemCommon';

const emptyUser = () => ({ email: '', name: '', surname: '', role: 'JuniorLocalUser', locationIds: [] });
const roleOptions = SITE_ROLES.map(role => ({ label: FLAG_ROLE_LABELS[role], value: role }));

const LocalUserManagement = ({ account, onAccessError }) => {
  const { customerId } = account;
  const load = useCallback(async () => {
    const [users, locations] = await Promise.all([siteUsersApi.list(customerId), flagLookupsApi.locations(customerId)]);
    return { users, locations };
  }, [customerId]);
  const resource = useRemoteResource(load, onAccessError);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [warning, setWarning] = useState('');
  const [saving, setSaving] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [forgotPasswordHelp, setForgotPasswordHelp] = useState(false);
  const [creationCheck, setCreationCheck] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expectedUser, setExpectedUser] = useState(null);
  const data = resource.data;
  const locations = data ? data.locations : [];
  const busy = saving || Boolean(resendingId);
  const pending = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (creationCheck && !resource.loading && data && data !== creationCheck.before) setCreationCheck(null);
  }, [creationCheck, resource.loading, data]);

  useEffect(() => {
    if (!expectedUser || resource.loading || !data || data === expectedUser.before) return;
    if (!data.users.some(user => user.id === expectedUser.id)) {
      setWarning('The account was created, but the refreshed site-user list does not include it. Reload the list before creating another account; the backend directory needs investigation.');
    }
    setExpectedUser(null);
  }, [data, resource.loading, expectedUser]);
  const open = (user) => {
    setEditor({ id: user ? user.id : null });
    setForm(user ? { email: user.email || '', name: user.name || '', surname: user.surname || '', role: user.role,
      locationIds: [...user.locationIds] } : emptyUser());
    setError(''); setErrors({}); setMessage('');
  };
  const close = () => { if (!busy) { setEditor(null); setForm(emptyUser()); setError(''); setErrors({}); } };
  const change = (field, value) => setForm(previous => ({ ...previous, [field]: value }));
  const save = async () => {
    if (pending.current || !editor || resource.loading || resource.error || (!editor.id && creationCheck)) return;
    const creating = !editor.id;
    const validation = validateSiteUser(form, locations, { singleLocation: creating });
    if (creating && data.users.some(user => user.email.toLowerCase() === form.email.trim().toLowerCase())) {
      validation.email = 'A site user with this email already exists. Close the form and use Resend Invitation for that user.';
    }
    setErrors(validation);
    if (Object.keys(validation).length) return;
    pending.current = true;
    setSaving(true); setError(''); setWarning(''); setForgotPasswordHelp(false);
    try {
      if (creating) {
        const user = await siteUsersApi.create(customerId, siteUserRequest(form));
        if (!mounted.current) return;
        setExpectedUser({ id: user.id, before: data });
        if (user.invitationEmailSent) {
          setInvitation(null); setMessage(user.message);
        } else {
          setMessage(''); setInvitation({ id: user.id, email: user.email });
        }
      } else {
        await siteUsersApi.update(customerId, editor.id, siteUserRequest(form));
        if (!mounted.current) return;
        setMessage('Site user updated.');
      }
      setForm(emptyUser()); setEditor(null); resource.reload();
    } catch (failure) {
      if (!mounted.current) return;
      onAccessError(failure);
      const status = failure.response && failure.response.status;
      const uncertain = creating && (!status || status >= 500 || (status >= 200 && status < 300));
      if (uncertain) setCreationCheck({ before: data });
      setError(status >= 200 && status < 300 ? failure.message : flagError(failure, 'Unable to save the site user.'));
      setErrors(flagFieldErrors(failure));
    } finally { pending.current = false; if (mounted.current) setSaving(false); }
  };
  const remove = async () => {
    if (pending.current) return;
    pending.current = true;
    setSaving(true); setError(''); setMessage('');
    try {
      await siteUsersApi.delete(customerId, deleteTarget.id);
      if (!mounted.current) return;
      if (invitation && invitation.id === deleteTarget.id) setInvitation(null);
      setDeleteTarget(null); setMessage('Site user deleted.'); resource.reload();
    } catch (failure) { if (mounted.current) { onAccessError(failure); setError(flagError(failure, 'Unable to delete the site user.')); } }
    finally { pending.current = false; if (mounted.current) setSaving(false); }
  };
  const openResend = user => {
    setError(''); setMessage(''); setForgotPasswordHelp(false); setResendTarget({ id: user.id, email: user.email });
  };
  const resend = async () => {
    if (pending.current || !resendTarget) return;
    pending.current = true;
    setResendingId(resendTarget.id); setError(''); setMessage('');
    try {
      const result = await siteUsersApi.resendInvitation(customerId, resendTarget.id);
      if (!mounted.current) return;
      setMessage(result.message);
      if (invitation && invitation.id === resendTarget.id) setInvitation(null);
      setResendTarget(null);
    } catch (failure) {
      if (!mounted.current) return;
      onAccessError(failure);
      const status = failure.response && failure.response.status;
      const body = failure.response && failure.response.data;
      const deliveryError = typeof body === 'string' ? body.trim() : body && body.error;
      const knownDeliveryErrors = ['The invitation email could not be sent. Please try again later.', 'Site-user invitations are not configured.'];
      setForgotPasswordHelp(status === 409);
      setError(status === 409 ? 'This user has already set a password. Ask them to use the existing Forgot Password flow instead.' :
        status === 503 && knownDeliveryErrors.includes(deliveryError) ? deliveryError :
          flagError(failure, 'Invitation delivery was not confirmed. Please try again later.'));
      setResendTarget(null);
    } finally { pending.current = false; if (mounted.current) setResendingId(null); }
  };
  return <FlagPage title="Local User Management" actions={<>
    <Button label="Refresh" icon="pi pi-refresh" className="p-button-text" disabled={busy || resource.loading} onClick={resource.reload} />
    <Button label="Create Site User" icon="pi pi-plus" className="p-button-success" onClick={() => open(null)}
      disabled={busy || Boolean(creationCheck) || resource.loading || Boolean(resource.error) || !locations.length} />
  </>}>
    <Feedback error={!editor ? error : ''} message={message}
      warning={[warning, invitation ? 'Site user created, but invitation delivery failed. Use Resend Invitation for this account; do not create it again.' : ''].filter(Boolean).join(' ')} />
    {invitation && <Button label="Resend Invitation" icon="pi pi-envelope" className="p-button-text"
      disabled={busy} onClick={() => openResend(invitation)} />}
    {forgotPasswordHelp && <p><Link to="/forgot-password" target="_blank" rel="noopener noreferrer">Forgot Password</Link> is available from the login page.</p>}
    {!editor && creationCheck && <p role="alert">The creation outcome is uncertain. Refresh the user list before creating another account.</p>}
    <ResourceState resource={resource}>
      {!locations.length && <p role="status">No customer locations are available. At least one customer-owned location is required to create a site user.</p>}
      <DataTable value={data ? data.users : []} dataKey="id" emptyMessage="No site users found." responsiveLayout="scroll">
        <Column field="email" header="Email" /><Column field="name" header="Name" /><Column field="surname" header="Surname" />
        <Column header="User level" body={user => FLAG_ROLE_LABELS[user.role] || 'Unsupported role'} />
        <Column header="Assigned locations" body={user => user.locationIds.map(id => {
          const location = locations.find(item => item.id === id);
          return location ? location.name : `Unavailable location (${id})`;
        }).join(', ')} />
        <Column header="Actions" body={user => <div className="flex gap-2">
          <Button label="Edit" icon="pi pi-pencil" className="p-button-text p-button-sm" disabled={busy || !SITE_ROLES.includes(user.role) || !locations.length}
            onClick={() => open(user)} />
          <Button label="Resend Invitation" icon="pi pi-envelope" className="p-button-text p-button-sm" disabled={busy || !SITE_ROLES.includes(user.role)}
            onClick={() => openResend(user)} />
          <Button label="Delete User" icon="pi pi-trash" className="p-button-danger p-button-text p-button-sm" disabled={busy}
            onClick={() => { setError(''); setDeleteTarget(user); }} />
        </div>} />
      </DataTable>
    </ResourceState>
    <p className="flag-note">Delete User permanently removes the account; it is not a soft-disable. Existing users change passwords using Forgot Password.</p>
    <Dialog header={editor && editor.id ? 'Edit Site User' : 'Create Site User'} visible={Boolean(editor)}
      className="flag-editor" modal closable={!busy} closeOnEscape={false} onHide={close}
      footer={<><Button label="Cancel" className="p-button-text" onClick={close} disabled={saving} />
        <Button label="Save User" icon="pi pi-check" onClick={save}
          disabled={busy || resource.loading || Boolean(resource.error) || Boolean(editor && !editor.id && creationCheck)} loading={saving} /></>}>
      <Feedback error={error || resource.error} />
      {editor && !editor.id && <p>The user will receive an email to choose their own password.</p>}
      {editor && !editor.id && creationCheck && <div role="alert">
        <p>The account may already exist. Reload the user list before submitting creation again.</p>
        <Button label="Reload User List" icon="pi pi-refresh" onClick={resource.reload} disabled={busy || resource.loading} />
      </div>}
      <Field id="site-user-email" label="Email" error={errors.email}>
        <InputText id="site-user-email" type="email" value={form.email} disabled={saving} onChange={event => change('email', event.target.value)} />
      </Field>
      <div className="flag-grid">{[['name', 'Name (optional)'], ['surname', 'Surname (optional)']].map(([field, label]) =>
        <Field key={field} id={`site-user-${field}`} label={label} error={errors[field]}>
          <InputText id={`site-user-${field}`} value={form[field]} disabled={saving} onChange={event => change(field, event.target.value)} />
        </Field>)}</div>
      <Field id="site-user-role" label="User level" error={errors.role}>
        <Dropdown inputId="site-user-role" value={form.role} options={roleOptions} disabled={saving} onChange={event => change('role', event.value)} />
      </Field>
      <Field id="site-user-locations" label="Assigned locations" error={errors.locationIds}>
        {editor && editor.id ? <MultiSelect inputId="site-user-locations" value={form.locationIds} options={locations} optionLabel="name" optionValue="id"
          display="chip" disabled={saving} onChange={event => change('locationIds', event.value)} placeholder="Select customer locations" /> :
          <Dropdown inputId="site-user-locations" value={form.locationIds[0] || null} options={locations} optionLabel="name" optionValue="id"
            disabled={saving} onChange={event => change('locationIds', event.value ? [event.value] : [])} placeholder="Select site for this user" />}
      </Field>
    </Dialog>
    <Dialog header="Resend Password-Setup Email?" visible={Boolean(resendTarget)} modal closable={!busy}
      onHide={() => !busy && setResendTarget(null)}
      footer={<><Button label="Cancel" className="p-button-text" disabled={busy} onClick={() => setResendTarget(null)} />
        <Button label="Send Invitation" icon="pi pi-envelope" onClick={resend} disabled={busy} loading={Boolean(resendingId)} /></>}>
      <p>Send a password-setup email to {resendTarget && resendTarget.email}? The backend checks whether this user can still receive an invitation.</p>
    </Dialog>
    <Dialog header="Delete Site User?" visible={Boolean(deleteTarget)} modal closable={!saving} onHide={() => !saving && setDeleteTarget(null)}
      footer={<><Button label="Cancel" disabled={saving} className="p-button-text" onClick={() => setDeleteTarget(null)} />
        <Button label="Delete User" className="p-button-danger" disabled={saving} loading={saving} onClick={remove} /></>}>
      <Feedback error={error} /><p>This permanently removes {deleteTarget && deleteTarget.email}'s Identity account. It is not a soft-disable.</p>
    </Dialog>
  </FlagPage>;
};
export default LocalUserManagement;
